/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Checkbox,
    CountryPicker,
    EmailValidation,
    FlexCol,
    FlexRow,
    Heading,
    RequiredValidation,
    Stack,
    StatePicker,
    TextField,
    EmailField,
} from 'sarsaparilla';
import get from 'lodash/get';

import { AllRoleTypesObj } from 'ui-internal-account';

import { getClassName } from '../../src/utils/utilsNew';

import {
    GROUP_DETAIL_ADDRESS1_PROP_PATH,
    GROUP_DETAIL_ADDRESS2_PROP_PATH,
    GROUP_DETAIL_ADDRESS_PROP_PATH,
    GROUP_DETAIL_CITY_PROP_PATH,
    GROUP_DETAIL_COUNTRY_PROP_PATH,
    GROUP_DETAIL_EMAIL_PROP_PATH,
    GROUP_DETAIL_FIRST_NAME_PROP_PATH,
    GROUP_DETAIL_LAST_NAME_PROP_PATH,
    GROUP_DETAIL_PHONE_PROP_PATH,
    GROUP_DETAIL_PROP_PATH,
    GROUP_DETAIL_SAVE_PROFILE_PROP_PATH,
    GROUP_DETAIL_STATE_PROP_PATH,
    GROUP_DETAIL_ZIPCODE_PROP_PATH,
    GROUP_MEMBERS_PROP_PATH,
    GROUP_MEMBERS_ADDRESS1_PROP_PATH,
    GROUP_MEMBERS_ADDRESS2_PROP_PATH,
    GROUP_MEMBERS_CITY_PROP_PATH,
    GROUP_MEMBERS_STATE_PROP_PATH,
    GROUP_MEMBERS_ZIPCODE_PROP_PATH,
    GROUP_MEMBERS_COUNTRY_PROP_PATH,
    GROUP_MEMBERS_EMAIL_PROP_PATH,
    GROUP_MEMBERS_FIRST_NAME_PROP_PATH,
    GROUP_MEMBERS_LAST_NAME_PROP_PATH,
    GROUP_MEMBERS_PHONE_PROP_PATH,
} from '../constants/propPaths';

const { SUPER_USER, PMO, BAH_ADMIN, CSR_SUPERVISOR, ATR, FACILITY_MANAGER } =
    AllRoleTypesObj;
const ROLES_FM_AND_ABOVE = [
    SUPER_USER,
    PMO,
    BAH_ADMIN,
    CSR_SUPERVISOR,
    ATR,
    FACILITY_MANAGER,
];

const defaultDataBindingMap = {
    [GROUP_DETAIL_ADDRESS1_PROP_PATH]: 'street_address1',
    [GROUP_DETAIL_ADDRESS2_PROP_PATH]: 'street_address2',
    [GROUP_DETAIL_ADDRESS_PROP_PATH]: 'permit_holder_address',
    [GROUP_DETAIL_CITY_PROP_PATH]: 'city',
    [GROUP_DETAIL_COUNTRY_PROP_PATH]: 'country',
    [GROUP_DETAIL_EMAIL_PROP_PATH]: 'email',
    [GROUP_DETAIL_FIRST_NAME_PROP_PATH]: 'first_name',
    [GROUP_DETAIL_LAST_NAME_PROP_PATH]: 'last_name',
    [GROUP_DETAIL_PHONE_PROP_PATH]: 'phone',
    [GROUP_DETAIL_PROP_PATH]: 'permit_holder',
    [GROUP_DETAIL_SAVE_PROFILE_PROP_PATH]: 'save_profile',
    [GROUP_DETAIL_STATE_PROP_PATH]: 'state_code',
    [GROUP_DETAIL_ZIPCODE_PROP_PATH]: 'postal_code',
};

const USA_COUNTRY_CODE = 'USA';
const CLASSNAME_SEPARATOR = '_';
const isValidUSAStateCode = (stateCode) =>
    !!stateCode && stateCode.length === 2 && stateCode === stateCode.toUpperCase();

export default (_, dataBindingsMap = {}) => {
    // create all prop mappings
    const dm = { ...defaultDataBindingMap, ...dataBindingsMap };

    const groupMemberFirstIndx = 0;
    const groupMemberFirst = `${dm[GROUP_MEMBERS_PROP_PATH]}[${groupMemberFirstIndx}]`;
    const groupMemberFirstHomeAddress = `${dm[GROUP_MEMBERS_PROP_PATH]}[${groupMemberFirstIndx}].home_address`;

    // Permit Holder information
    const firstNamePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_FIRST_NAME_PROP_PATH]}`;
    const lastNamePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_LAST_NAME_PROP_PATH]}`;
    const emailPropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_EMAIL_PROP_PATH]}`;
    const phonePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_PHONE_PROP_PATH]}`;

    // Address information
    const countryPropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_COUNTRY_PROP_PATH]}`;
    const postalCodePropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ZIPCODE_PROP_PATH]}`;
    const address1PropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ADDRESS1_PROP_PATH]}`;
    const address2PropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_ADDRESS2_PROP_PATH]}`;
    const statePropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_STATE_PROP_PATH]}`;
    const cityPropPath = `${dm[GROUP_DETAIL_ADDRESS_PROP_PATH]}.${dm[GROUP_DETAIL_CITY_PROP_PATH]}`;

    // Group Members
    const gmFirstNamePropPath = `${groupMemberFirst}.${dm[GROUP_MEMBERS_FIRST_NAME_PROP_PATH]}`;
    const gmLastNamePropPath = `${groupMemberFirst}.${dm[GROUP_MEMBERS_LAST_NAME_PROP_PATH]}`;
    const gmEmailPropPath = `${groupMemberFirst}.${dm[GROUP_MEMBERS_EMAIL_PROP_PATH]}`;
    const gmPhonePropPath = `${groupMemberFirst}.${dm[GROUP_MEMBERS_PHONE_PROP_PATH]}`;
    const gmAddress1PropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_ADDRESS1_PROP_PATH]}`;
    const gmAddress2PropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_ADDRESS2_PROP_PATH]}`;
    const gmCityPropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_CITY_PROP_PATH]}`;
    const gmStatePropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_STATE_PROP_PATH]}`;
    const gmPostalCodePropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_ZIPCODE_PROP_PATH]}`;
    const gmCountryPropPath = `${groupMemberFirstHomeAddress}.${dm[GROUP_MEMBERS_COUNTRY_PROP_PATH]}`;

    const permitHolderGroupMemberMap = {
        [firstNamePropPath]: gmFirstNamePropPath,
        [lastNamePropPath]: gmLastNamePropPath,
        [emailPropPath]: gmEmailPropPath,
        [phonePropPath]: gmPhonePropPath,
        [address1PropPath]: gmAddress1PropPath,
        [address2PropPath]: gmAddress2PropPath,
        [cityPropPath]: gmCityPropPath,
        [statePropPath]: gmStatePropPath,
        [postalCodePropPath]: gmPostalCodePropPath,
        [countryPropPath]: gmCountryPropPath,
    };

    const saveToProfilePropPath = `${dm[GROUP_DETAIL_PROP_PATH]}.${dm[GROUP_DETAIL_SAVE_PROFILE_PROP_PATH]}`;

    // Label Information
    const firstNameLabelId = dm[GROUP_DETAIL_FIRST_NAME_PROP_PATH];
    const lastNameLabelId = dm[GROUP_DETAIL_LAST_NAME_PROP_PATH];
    const emailLabelId = dm[GROUP_DETAIL_EMAIL_PROP_PATH];

    const phoneLabelId = dm[GROUP_DETAIL_PHONE_PROP_PATH];
    const countryLabelId = dm[GROUP_DETAIL_COUNTRY_PROP_PATH];
    const postalLabelId = dm[GROUP_DETAIL_ZIPCODE_PROP_PATH];
    const address1LabelId = dm[GROUP_DETAIL_ADDRESS1_PROP_PATH];
    const stateNameLabelId = dm[GROUP_DETAIL_STATE_PROP_PATH];
    const stateCodeLabelId = dm[GROUP_DETAIL_STATE_PROP_PATH];
    const cityLabelId = dm[GROUP_DETAIL_CITY_PROP_PATH];

    function GroupDetailNew(props) {
        const reservationId = props.reservation.id;
        const { updatePendingReservation } = props.pActions;

        const selectedCountry = get(props.reservation, countryPropPath, '');
        const selectedState = get(props.reservation, statePropPath, '');
        useEffect(() => {
            if (!selectedCountry) {
                updatePendingReservation(
                    reservationId,
                    countryPropPath,
                    USA_COUNTRY_CODE
                );
            } else if (
                selectedState &&
                selectedCountry === USA_COUNTRY_CODE &&
                !isValidUSAStateCode(selectedState)
            ) {
                updatePendingReservation(reservationId, statePropPath, '');
            }
        }, [selectedCountry, selectedState, reservationId, updatePendingReservation]);

        const onFormValidate = (isInValid, error, path) =>
            props.pActions.formValidationChange?.(
                `${reservationId}_${path}`,
                isInValid ? error : null
            );

        const getCheckChangeAction = (propPath) => (e) => {
            if (props.shouldValidateOnBEOnly && props.pActions.formValidationChange) {
                props.pActions.formValidationChange(propPath, null);
            }
            return updatePendingReservation(reservationId, propPath, e.target.checked);
        };

        const getComponentClassName = (...path) => {
            if (props.shouldValidateOnBEOnly) {
                // get only path[0], disregard 'req' or 'format' postfixes
                return getClassName(get(props.validation, path[0]), true);
            }
            return getClassName(
                get(props.validation, [reservationId, ...path].join(CLASSNAME_SEPARATOR)),
                props.displayValidationError
            );
        };

        const getValidationAction = (propPath) => (isInValid, error) =>
            onFormValidate(isInValid, error, propPath);

        const getValueChangeAction = (propPath) => (e) => {
            if (permitHolderGroupMemberMap[propPath]) {
                // update group_members object
                const newPropPath = permitHolderGroupMemberMap[propPath];
                updatePendingReservation(reservationId, newPropPath, e.target.value);
            }

            if (props.shouldValidateOnBEOnly && props.pActions.formValidationChange) {
                props.pActions.formValidationChange(propPath, null);
            }
            return updatePendingReservation(reservationId, propPath, e.target.value);
        };

        const handleAddress1Change = getValueChangeAction(address1PropPath);
        const handleAddress1Validation = getValidationAction(address1PropPath);

        const handleAddress2Change = getValueChangeAction(address2PropPath);

        const handleCityChange = getValueChangeAction(cityPropPath);
        const handleCityValidation = getValidationAction(cityPropPath);

        const handleCountryChange = getValueChangeAction(countryPropPath);
        const handleCountryValidation = getValidationAction(countryPropPath);

        const handleEmailChange = getValueChangeAction(emailPropPath);
        const handleEmailFormatValidation = getValidationAction(
            `${emailPropPath}_format`
        );
        const handleEmailValidation = getValidationAction(`${emailPropPath}_req`);

        const handleFirstNameChange = getValueChangeAction(firstNamePropPath);
        const handleFirstNameValidation = getValidationAction(firstNamePropPath);

        const handleLastNameChange = getValueChangeAction(lastNamePropPath);
        const handleLastNameValidation = getValidationAction(lastNamePropPath);

        const handlePhoneChange = getValueChangeAction(phonePropPath);
        const handlePhoneValidation = getValidationAction(`${phonePropPath}_req`);

        const handlePostalCodeChange = getValueChangeAction(postalCodePropPath);
        const handlePostalCodeValidation = getValidationAction(postalCodePropPath);

        const handleSaveChangesChange = getCheckChangeAction(saveToProfilePropPath);

        const handleStateChange = getValueChangeAction(statePropPath);
        const handleStateValidation = getValidationAction(statePropPath);

        const isReadOnly = () => !props.hasPermission || !props.isInternal;

        const shouldUseStatePicker = () =>
            get(props.reservation, countryPropPath, '') === USA_COUNTRY_CODE;

        // For internal users, only show "Save changes to my account" for FM and above.
        const roleType = useSelector(
            (state) => state.selectedLocationRole?.role?.role_type
        );

        const canUpdateAccount = !roleType || ROLES_FM_AND_ABOVE.includes(roleType);

        const renderSavePermitHolderChanges = () => {
            const { reservation } = props;
            const isChecked = get(reservation, saveToProfilePropPath, false);

            return (
                <Checkbox
                    id="save-leader-checkbox"
                    className="mt-2"
                    isChecked={isChecked}
                    label="Save changes to my account"
                    onChange={handleSaveChangesChange}
                />
            );
        };

        const renderPermitHolder = () => {
            const {
                isValidatingAlternateHolder,
                reservation,
                shouldValidateOnBEOnly,
                addFieldRef,
                name,
            } = props;
            const readOnly = isReadOnly();

            const firstName = get(reservation, firstNamePropPath, '');
            const lastName = get(reservation, lastNamePropPath, '');
            const email = get(reservation, emailPropPath, '');
            const phone = get(reservation, phonePropPath, '');

            const permitHolderTitle = isValidatingAlternateHolder ? (
                <p>
                    Please validate your contact information below. For this reservation,
                    you will not be able to change this information later.
                </p>
            ) : (
                <Heading headingLevel={2} appearance="h5">
                    Permit Holder
                </Heading>
            );

            const marginBottonOnMdOrSmaller = 'mb-2 mb-md-0';

            return (
                <Stack space="md" className="per-group-detail-form-permit-holder">
                    {permitHolderTitle}
                    <FlexRow>
                        <FlexCol md="auto" className={marginBottonOnMdOrSmaller}>
                            <TextField
                                ref={addFieldRef?.(name, 0)}
                                className={getComponentClassName(firstNamePropPath)}
                                id={firstNameLabelId}
                                isDisabled={readOnly}
                                isInvalid={!!getComponentClassName(firstNamePropPath)}
                                isRequired
                                label="First Name"
                                onChange={handleFirstNameChange}
                                value={firstName}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="First Name"
                                    displayInSummary
                                    onValidated={handleFirstNameValidation}
                                    value={firstName}
                                />
                            )}
                        </FlexCol>
                        <FlexCol md="auto">
                            <TextField
                                ref={addFieldRef?.(name, 1)}
                                className={getComponentClassName(lastNamePropPath)}
                                id={lastNameLabelId}
                                isDisabled={readOnly}
                                isRequired
                                isInvalid={!!getComponentClassName(lastNamePropPath)}
                                label="Last Name"
                                onChange={handleLastNameChange}
                                value={lastName}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="Last Name"
                                    displayInSummary
                                    onValidated={handleLastNameValidation}
                                    value={lastName}
                                />
                            )}
                        </FlexCol>
                    </FlexRow>
                    <FlexRow>
                        <FlexCol md="auto" className={marginBottonOnMdOrSmaller}>
                            <EmailField
                                ref={addFieldRef?.(name, 2)}
                                className={`${getComponentClassName(emailPropPath, 'req')}${getComponentClassName(emailPropPath, 'format')}`}
                                id={emailLabelId}
                                isDisabled
                                isRequired
                                isInvalid={
                                    !!getComponentClassName(emailPropPath, 'req') ||
                                    !!getComponentClassName(emailPropPath, 'format')
                                }
                                label="Email"
                                onChange={handleEmailChange}
                                value={email}
                            />
                            {!shouldValidateOnBEOnly && (
                                <>
                                    <RequiredValidation
                                        controlLabel="Email"
                                        displayInSummary
                                        onValidated={handleEmailValidation}
                                        value={email}
                                    />
                                    <EmailValidation
                                        controlLabel="Email"
                                        displayInSummary
                                        onValidated={handleEmailFormatValidation}
                                        value={email}
                                    />
                                </>
                            )}
                        </FlexCol>
                        <FlexCol md="auto">
                            <TextField
                                ref={addFieldRef?.(name, 3)}
                                className={`${getComponentClassName(phonePropPath, 'req')}${getComponentClassName(phonePropPath, 'format')}`}
                                id={`${phoneLabelId}_req`}
                                isRequired
                                isInvalid={
                                    !!getComponentClassName(phonePropPath, 'req') ||
                                    !!getComponentClassName(phonePropPath, 'format')
                                }
                                label="Phone Number"
                                onChange={handlePhoneChange}
                                value={phone}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="Phone Number"
                                    displayInSummary
                                    onValidated={handlePhoneValidation}
                                    value={phone}
                                />
                            )}
                        </FlexCol>
                    </FlexRow>
                </Stack>
            );
        };

        const renderLocation = () => {
            const {
                reservation,
                shouldValidateOnBEOnly,
                addFieldRef,
                name,
                addressTitle,
            } = props;

            const city = get(reservation, cityPropPath, '');
            const stateName = get(reservation, statePropPath, '');
            const stateCode = get(reservation, statePropPath, '');
            const zipCode = get(reservation, postalCodePropPath, '');
            const country = get(reservation, countryPropPath, '');
            const streetAddress = get(reservation, address1PropPath, '');
            const aptSuiteNumber = get(reservation, address2PropPath, '');

            const marginBottonOnMdOrSmaller = 'mb-2 mb-md-0';

            return (
                <Stack space="md" className="per-group-detail-form-location pt-4">
                    <Heading headingLevel={2} appearance="h5">
                        {addressTitle || 'Address'}
                    </Heading>
                    <FlexRow>
                        <FlexCol md={6}>
                            <CountryPicker
                                ref={addFieldRef?.(name, 4)}
                                className={getComponentClassName(countryPropPath)}
                                isInvalid={!!getComponentClassName(countryPropPath)}
                                value={country}
                                id={countryLabelId}
                                isRequired
                                onChange={handleCountryChange}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="Country"
                                    displayInSummary
                                    onValidated={handleCountryValidation}
                                    value={country}
                                />
                            )}
                        </FlexCol>
                    </FlexRow>
                    <FlexRow>
                        <FlexCol md="auto" className={marginBottonOnMdOrSmaller}>
                            <TextField
                                ref={addFieldRef?.(name, 5)}
                                className={getComponentClassName(address1PropPath)}
                                id={address1LabelId}
                                isRequired
                                isInvalid={!!getComponentClassName(address1PropPath)}
                                label="Street Address"
                                onChange={handleAddress1Change}
                                value={streetAddress}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="Street Address"
                                    displayInSummary
                                    onValidated={handleAddress1Validation}
                                    value={streetAddress}
                                />
                            )}
                        </FlexCol>
                        <FlexCol md="auto">
                            <TextField
                                className={getComponentClassName(address2PropPath)}
                                id="suite-number"
                                label="Apt or Suite Number"
                                onChange={handleAddress2Change}
                                value={aptSuiteNumber}
                            />
                        </FlexCol>
                    </FlexRow>
                    <FlexRow>
                        <FlexCol md={6} xs={12} className={marginBottonOnMdOrSmaller}>
                            <TextField
                                ref={addFieldRef?.(name, 6)}
                                className={getComponentClassName(cityPropPath)}
                                id={cityLabelId}
                                isRequired
                                isInvalid={!!getComponentClassName(cityPropPath)}
                                label="City / Town"
                                onChange={handleCityChange}
                                value={city}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="City / Town"
                                    displayInSummary
                                    onValidated={handleCityValidation}
                                    value={city}
                                />
                            )}
                        </FlexCol>
                        <FlexCol md={3} xs={12} className={marginBottonOnMdOrSmaller}>
                            {shouldUseStatePicker() ? (
                                <>
                                    <StatePicker
                                        ref={addFieldRef?.(name, 7)}
                                        className={getComponentClassName(statePropPath)}
                                        isInvalid={!!getComponentClassName(statePropPath)}
                                        value={stateCode}
                                        id={stateCodeLabelId}
                                        isRequired
                                        label="State / Province"
                                        onChange={handleStateChange}
                                    />
                                    {!shouldValidateOnBEOnly && (
                                        <RequiredValidation
                                            controlLabel="State / Province"
                                            displayInSummary
                                            onValidated={handleStateValidation}
                                            value={stateCode}
                                        />
                                    )}
                                </>
                            ) : (
                                <>
                                    <TextField
                                        ref={addFieldRef?.(name, 7)}
                                        className={getComponentClassName(statePropPath)}
                                        id={stateNameLabelId}
                                        isRequired
                                        isInvalid={!!getComponentClassName(statePropPath)}
                                        label="State / Province"
                                        onChange={handleStateChange}
                                        value={stateName}
                                    />
                                    {!shouldValidateOnBEOnly && (
                                        <RequiredValidation
                                            controlLabel="State / Province"
                                            displayInSummary
                                            onValidated={handleStateValidation}
                                            value={stateName}
                                        />
                                    )}
                                </>
                            )}
                        </FlexCol>
                        <FlexCol md={3}>
                            <TextField
                                ref={addFieldRef?.(name, 8)}
                                className={getComponentClassName(postalCodePropPath)}
                                id={postalLabelId}
                                isRequired
                                isInvalid={!!getComponentClassName(postalCodePropPath)}
                                label="Zip / Postal Code"
                                onChange={handlePostalCodeChange}
                                value={zipCode}
                            />
                            {!shouldValidateOnBEOnly && (
                                <RequiredValidation
                                    controlLabel="Zip / Postal Code"
                                    displayInSummary
                                    onValidated={handlePostalCodeValidation}
                                    value={zipCode}
                                />
                            )}
                        </FlexCol>
                    </FlexRow>
                </Stack>
            );
        };

        return (
            <div className="per-group-detail-form per-section-group">
                {!props.isLocationOnly && renderPermitHolder()}
                {renderLocation()}
                {!props.isLocationOnly &&
                    !props.disableSavePermitHolderChanges &&
                    canUpdateAccount &&
                    renderSavePermitHolderChanges()}
            </div>
        );
    }

    GroupDetailNew.propTypes = {
        pActions: PropTypes.object,
        validation: PropTypes.object,
        shouldValidateOnBEOnly: PropTypes.bool,

        displayValidationError: PropTypes.bool,
        hasPermission: PropTypes.bool,
        isValidatingAlternateHolder: PropTypes.bool,
        isInternal: PropTypes.bool,
        reservation: PropTypes.object,
        name: PropTypes.string,
        addFieldRef: PropTypes.func,
        isLocationOnly: PropTypes.bool,
        addressTitle: PropTypes.string,

        disableSavePermitHolderChanges: PropTypes.bool,
    };

    return React.memo(GroupDetailNew);
};
